import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

/** Absolute origin every canonical URL is built from. No trailing slash. */
export const SITE_ORIGIN = 'https://hxplayer-music.com';

@Injectable({
  providedIn: 'root',
})
export class MetaTagsService {
  private readonly document = inject(DOCUMENT);

  constructor(
    private titleService: Title,
    private metaService: Meta,
  ) {}

  /**
   * Points `<link rel="canonical">` and `og:url` at the current route.
   *
   * Written through `DOCUMENT` so it applies during prerendering as well as in
   * the browser. Without this every page inherits the hardcoded canonical in
   * `index.html` and reads to Google as a duplicate of the home page.
   *
   * @param path - App path, e.g. `/radio/jazz-fm`. Query and fragment excluded.
   */
  setCanonicalUrl(path: string): void {
    const url = !path || path === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`;
    const head = this.document.head;
    if (!head) {
      return;
    }

    let link = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', url);

    this.metaService.updateTag({ property: 'og:url', content: url });
  }

  setTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  setMetatags(
    title: string,
    description: string,
    ogTitle?: string,
    ogDescription?: string,
    ogImage?: string,
    ogUrl?: string,
    keywords?: string,
  ): void {
    // Set title
    this.setTitle(title);

    // Set meta description
    this.metaService.updateTag({
      name: 'description',
      content: description,
    });

    // Set keywords
    if (keywords) {
      this.metaService.updateTag({
        name: 'keywords',
        content: keywords,
      });
    }

    // Set Open Graph tags for better social sharing
    this.metaService.updateTag({
      property: 'og:title',
      content: ogTitle || title,
    });

    this.metaService.updateTag({
      property: 'og:description',
      content: ogDescription || description,
    });

    if (ogImage) {
      this.metaService.updateTag({
        property: 'og:image',
        content: ogImage,
      });
    }

    if (ogUrl) {
      this.metaService.updateTag({
        property: 'og:url',
        content: ogUrl,
      });
    }

    this.metaService.updateTag({
      property: 'og:type',
      content: 'website',
    });

    // Set Twitter card tags
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.metaService.updateTag({
      name: 'twitter:title',
      content: title,
    });

    this.metaService.updateTag({
      name: 'twitter:description',
      content: description,
    });

    if (ogImage) {
      this.metaService.updateTag({
        name: 'twitter:image',
        content: ogImage,
      });
    }
  }

  setDefaultMetatags(): void {
    this.setMetatags(
      'HX Music Player - Listen to Radio Stations Online',
      'Discover and listen to your favorite radio stations across multiple genres including rock, pop, jazz, country, and more.',
      'HX Player - Music & Online Radio',
      'Discover and listen to your favorite radio stations online',
      undefined,
      undefined,
      'radio, music, streaming, genres, online radio, music player',
    );
  }
}
